import { Inject, Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { API_BASE_URL } from '@core/config/api.config';
import { AuthSessionService } from '@core/auth/auth-session.service';
import { ActionCableClient, buildActionCableUrl } from '@core/realtime/action-cable-client';
import { ToastService } from 'src/app/shared/ui/toast/toast.service';
import { OrderNotificationItem, OrderNotificationKind } from './order-notification.model';
import { playOrderNotificationSound, unlockOrderNotificationSound } from './order-notification-sound';

const MAX_ITEMS = 20;

interface RawRealtimeMessage {
  event?: unknown;
  order_id?: unknown;
  service_request_id?: unknown;
  number?: unknown;
  customer_name?: unknown;
  updated_at?: unknown;
}

function readReceivedAt(message: RawRealtimeMessage): string {
  return typeof message.updated_at === 'string' ? message.updated_at : new Date().toISOString();
}

/**
 * Aviso global de pedidos y solicitudes nuevas (sonido + Notification API + badge).
 * Reutiliza `SalesBoardChannel` (compañía); el canal acepta sales_board o module_services.
 */
@Injectable({ providedIn: 'root' })
export class OrderNotificationsService {
  private readonly authSession = inject(AuthSessionService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  private client: ActionCableClient | null = null;
  private unlockListenerAttached = false;

  readonly unreadCount = signal(0);
  readonly items = signal<OrderNotificationItem[]>([]);
  readonly browserPermission = signal<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );

  constructor(@Inject(API_BASE_URL) private readonly apiBaseUrl: string) {}

  connect(companyId: number): void {
    const token = this.authSession.token();
    if (!token) {
      return;
    }

    this.disconnect();
    this.attachSoundUnlockListener();
    this.requestBrowserPermission();

    this.client = new ActionCableClient({
      url: buildActionCableUrl(this.apiBaseUrl, token, companyId),
      channel: 'SalesBoardChannel',
      onMessage: (message) => this.handleMessage(message),
    });
    this.client.connect();
  }

  disconnect(): void {
    this.client?.disconnect();
    this.client = null;
  }

  markAllRead(): void {
    this.unreadCount.set(0);
  }

  requestBrowserPermission(): void {
    if (typeof Notification === 'undefined' || Notification.permission !== 'default') {
      return;
    }
    void Notification.requestPermission().then((permission) => this.browserPermission.set(permission));
  }

  private attachSoundUnlockListener(): void {
    if (this.unlockListenerAttached || typeof document === 'undefined') {
      return;
    }
    this.unlockListenerAttached = true;
    document.addEventListener('pointerdown', () => unlockOrderNotificationSound(), { once: true });
  }

  private handleMessage(message: unknown): void {
    if (!message || typeof message !== 'object') {
      return;
    }
    const raw = message as RawRealtimeMessage;
    if (raw.event === 'order_created' && raw.order_id != null) {
      this.pushNotification({
        kind: 'order',
        entityId: raw.order_id as number | string,
        number: Number(raw.number ?? 0) || 0,
        receivedAt: readReceivedAt(raw),
      });
      return;
    }
    if (raw.event === 'service_request_created' && raw.service_request_id != null) {
      this.pushNotification({
        kind: 'service_request',
        entityId: raw.service_request_id as number | string,
        number: Number(raw.number ?? 0) || 0,
        receivedAt: readReceivedAt(raw),
        customerName:
          typeof raw.customer_name === 'string' ? raw.customer_name.trim() : undefined,
      });
    }
  }

  private pushNotification(input: {
    kind: OrderNotificationKind;
    entityId: number | string;
    number: number;
    receivedAt: string;
    customerName?: string;
  }): void {
    const item: OrderNotificationItem = {
      id: `${input.kind}-${input.entityId}-${input.receivedAt}`,
      kind: input.kind,
      entityId: input.entityId,
      number: input.number,
      receivedAt: input.receivedAt,
      customerName: input.customerName,
    };

    this.items.update((current) => [item, ...current].slice(0, MAX_ITEMS));
    this.unreadCount.update((count) => count + 1);

    playOrderNotificationSound();

    const i18nKey =
      input.kind === 'service_request'
        ? 'header.notifications.serviceRequestReceived'
        : 'header.notifications.orderReceived';
    const title = this.translate.instant(`${i18nKey}.title`, { number: input.number });
    const body = this.translate.instant(`${i18nKey}.subtitle`, {
      name: input.customerName || '',
    });
    this.toast.info(title, body);
    this.showBrowserNotification(title, body, input.kind);
  }

  private showBrowserNotification(
    title: string,
    body: string,
    kind: OrderNotificationKind
  ): void {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      return;
    }
    if (typeof document !== 'undefined' && document.visibilityState === 'visible' && document.hasFocus()) {
      return;
    }
    try {
      new Notification(title, {
        body,
        tag: kind === 'service_request' ? 'service-request-received' : 'order-received',
      });
    } catch {
      // Algunos navegadores (p. ej. móviles) restringen `new Notification` fuera de un service worker.
    }
  }
}
